import React from "react";
import { Tooltip } from "@mui/material";
import { UseYetiStats } from "../../hooks/company";
import { formatNumber } from "../../helpers/formatter";
import { formatKpiPercent, isBidAccuracyKpi } from "../../helpers/scoreAggregate";
import MountainClimb, { GREEN, RED, RidgeDefs } from "./climb";
import { climbFracs } from "./pace";
import "../../pages/Homepage/customize/customize.css";

/**
 * Beat the Yeti — the workspace's active gamified metrics, each shown as a
 * "mountain climb" tile (Victor's Figma design, node 61:160): one consistent
 * mountain (faint layered ridges + a climb line) on every tile, with the line
 * turning GREEN when the player is beating the Yeti and RED when behind, and
 * two dots marking positions on the climb — navy = you, pink = the Yeti.
 *
 * No new data: it reads the same per-KPI player amount / Yeti pace / points
 * the "Team Performance vs. The Yeti" card already uses (UseYetiStats). The
 * mountain vectors are lifted 1:1 from the Figma export so the look matches;
 * only the line colour and the two dot positions change per metric.
 */

/* The climb art, the palette and the dot maths now live beside this file
   (./climb, ./pace) so All Department Metrics can render the SAME tile at a
   smaller size rather than a lookalike. Nothing about this card changed. */

// periodIdx → the yeti-score bucket, mirroring the dashboard filter order.
const IDX_TO_BUCKET = { 0: "yesterday", 1: "today", 2: "week", 4: "lastWeek", 3: "month", 5: "lastMonth" };

/* Human-readable "how this KPI works", generated from the metric's own config —
 * shown when hovering the ⓘ on a tile. Uses the same points precedence the
 * scoring code does (pointValue ratio → pointsRule pair → legacy pointScale). */
const kpiExplainer = (kpi) => {
  const fmtV = (v) => (kpi.kind === "%" ? formatKpiPercent(kpi, Number(v) || 0) : formatNumber(Number(v) || 0, kpi.kind));
  const KIND_LABELS = { $: "a dollar amount", "%": "a percentage rate" };
  const kindLabel = KIND_LABELS[kpi.kind] || "a count";
  let calcLabel = "summed for the period";
  if (kpi.calc === "avg") calcLabel = "averaged across entries";
  else if (kpi.kind === "%") calcLabel = "tracked for the period"; // rates aren't sums
  const lines = [`Measured as ${kindLabel}, ${calcLabel}.`];

  // Mirrors the scoring precedence in helpers/kpiPoints.js: the pointValue /
  // pointScale ratio only counts when BOTH are set and positive. A lone
  // legacy pointScale (e.g. Closed Rate's 5000) never awards points by
  // itself, so it must not be described as a rule ("every 500,000% earns
  // 0 pts"). When no rule applies, say nothing about points.
  const rule = kpi.parameters?.pointsRule;
  const ptsValue = Number(kpi.pointValue) || 0;
  const ptsScale = Number(kpi.pointScale) || 0;
  if (ptsValue > 0 && ptsScale > 0) {
    lines.push(`Points: every ${fmtV(ptsScale)} earns ${ptsValue} pt${ptsValue === 1 ? "" : "s"}.`);
  } else if (rule && Number(rule.metricAmount) > 0) {
    lines.push(
      `Points: achieving ${fmtV(rule.metricAmount)} earns ${Number(rule.pointsEarned) || 0} pts.`,
    );
  } else if (ptsValue > 0) {
    lines.push(`Points: ${ptsValue} pt${ptsValue === 1 ? "" : "s"} per unit.`);
  }

  if (Number(kpi.target) > 0) lines.push(`Monthly target: ${fmtV(kpi.target)}.`);
  if ((kpi.direction ?? 1) < 0 || kpi.metricType === "anchor") lines.push("Lower is better on this metric.");
  lines.push("The Yeti's dot climbs at the target pace — stay ahead of it to win.");
  return lines.join(" ");
};

const INFO_TOOLTIP_PROPS = {
  arrow: true,
  placement: "top",
  componentsProps: {
    tooltip: {
      sx: {
        fontFamily: "DM Sans",
        fontSize: "12px",
        lineHeight: 1.45,
        fontWeight: 400,
        maxWidth: 260,
        padding: "10px 12px",
        backgroundColor: "#0F172A",
        borderRadius: "8px",
      },
    },
    arrow: { sx: { color: "#0F172A" } },
  },
};

const MountainTile = ({ kpi, yeti, bucket = "month" }) => {
  const playerAmt = Number(kpi.amount) || 0;
  const yetiBucket = yeti?.[bucket] || yeti?.month;
  const yetiAmt = Number(yetiBucket?.[kpi._id]?.amt) || 0;
  const playerPts = Number(kpi.points) || 0;
  const yetiPts = Number(yetiBucket?.[kpi._id]?.pts) || 0;

  // Winning mirrors the Yeti card's rule: player points vs the Yeti's points.
  const winning = playerPts >= yetiPts;
  const diff = playerAmt - yetiAmt;

  // Positions on the climb — see pace.js for the shared rule.
  const fracs = climbFracs({ kpi, playerAmt, yetiAmt, bucket, totalDays: yeti?.totalDays });

  // Whole numbers only on the tile (matches the Yeti scorecard tiles) — no
  // trailing cents / decimal percents. % amounts are stored as fractions
  // (0.4314 = 43.14%), so rounding the raw fraction would collapse a real
  // rate to 0% or 100% — round those to the nearest whole PERCENT instead.
  // Quality Bid Accuracy is the one exception, kept to one decimal.
  const fmt = (val) => {
    const n = Number(val) || 0;
    if (kpi.kind === "%") return formatKpiPercent(kpi, n);
    return formatNumber(Math.round(n) || 0, kpi.kind); // `|| 0` normalizes -0 so a tiny deficit can't print "-0"
  };
  // Delta color/arrow follow the shown amount difference, not the points race:
  // a negative delta must read red even when points still say "winning".
  // Sign is judged on the same rounded value the tile prints, so a deficit
  // that displays as "0" can't carry a red down-arrow. Bid Accuracy rounds to
  // one decimal (0.1%) instead of a whole percent for this sign check too, to
  // stay consistent with the one-decimal value it's paired with.
  let roundedDiff = Math.round(diff);
  if (kpi.kind === "%") {
    roundedDiff = isBidAccuracyKpi(kpi) ? Math.round(diff * 1000) / 1000 : Math.round(diff * 100) / 100;
  }
  const ahead = roundedDiff >= 0;

  return (
    <div className="gmbw-mt-tile">
      <MountainClimb playerFrac={fracs.player} yetiFrac={fracs.yeti} winning={winning} />
      <div className="gmbw-mt-info">
        <div className="gmbw-mt-name">
          <span className="gmbw-mt-name-text">{kpi.title}</span>
          <Tooltip title={kpiExplainer(kpi)} {...INFO_TOOLTIP_PROPS}>
            <svg className="gmbw-mt-i" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ cursor: "help" }}><circle cx="12" cy="12" r="9" /><path d="M12 11v5" strokeLinecap="round" /><circle cx="12" cy="8" r="0.6" fill="currentColor" stroke="none" /></svg>
          </Tooltip>
        </div>
        <div className="gmbw-mt-val">{fmt(playerAmt)}</div>
        <div className="gmbw-mt-delta" style={{ color: ahead ? GREEN : RED }}>
          {roundedDiff > 0 ? "+" : ""}{fmt(diff)} {ahead ? "↑" : "↓"}
        </div>
      </div>
    </div>
  );
};

const BeatTheYeti = ({ kpis = [], title, periodIdx, yeti: yetiStats = null }) => {
  // Prefer the pace ScopedBeatYeti already combined across this card's scoped
  // workspaces (avg/sum) — this prop used to be silently dropped, which is
  // the same bug that left "Team Performance vs. The Yeti" reading a 0 goal
  // whenever a tile was scoped to something other than the currently-selected
  // nav workspace, or combined 2+ workspaces. Surfaces that render
  // BeatTheYeti without a scoped card pass no `yeti` and keep the live hook.
  const globalYeti = UseYetiStats();
  const yeti = yetiStats || globalYeti;
  const active = (kpis || []).filter((k) => k && !k.isDraft);
  const bucket = IDX_TO_BUCKET[periodIdx] || "month";

  return (
    <div className="gmbw-sales-card">
      <div className="gmbw-sales-head">
        <span className="gmbw-sales-title">{title || "Beat the Yeti"}</span>
        <span className="gmbw-mt-legend">
          <span><i className="gmbw-mt-d you" />You</span>
          <span><i className="gmbw-mt-d yeti" />Yeti</span>
        </span>
      </div>
      <div className="gmbw-sales-body" style={{ overflowY: "auto" }}>
        <RidgeDefs />
        {active.length ? (
          <div className="gmbw-mt-grid">
            {active.map((k) => (
              <MountainTile key={k._id || k.title} kpi={k} yeti={yeti} bucket={bucket} />
            ))}
          </div>
        ) : (
          <div className="gmbw-zero">No gamified metrics with targets yet — add metrics to see the climb.</div>
        )}
      </div>
    </div>
  );
};

export default BeatTheYeti;
